import i18next from 'i18next';
import { CboRole, FeatureFlag, Package } from '@cbo/shared-library';
import { useFeatureFlags } from '../../../utils/hooks/useFeatureFlag';
import isPermitted, { isEligibletoShow } from '../../../lib/permissions';
import { useUsers } from '../../../contexts/userContext';
import routes from '../../../constants/routes';
import { NavItem } from '.';
import { usePackages } from '../../../contexts/packageContext';
import useCfcOrg from '../../../utils/hooks/useCfcOrg';

export default function useSettingsRoutes(): NavItem[] {
  const [
    cboGeneralLedgerAccounts,
    cboHideAlohaEssentialOrgs,
    cboTipShareDistribution,
    cboCustomMetrics,
    cboInventoryValuation,
  ] = useFeatureFlags(
    FeatureFlag.generalLedgerAccounts,
    FeatureFlag.hideAlohaEssentialOrgs,
    FeatureFlag.tipShareDistribution,
    FeatureFlag.customMetrics,
    FeatureFlag.inventoryValuation
  );
  const user = useUsers();
  const packages = usePackages();
  const { isCfcOrg } = useCfcOrg(user.fullyAuthenticated === 'authenticated');
  const hasStarterPackage = packages[Package.STARTER];
  const hasCoreAnalytics = packages[Package.ADVANCED_ANALYTICS];

  const settingsRoutes: NavItem[] = [];
  const organizationSettingsChildren: NavItem[] = [];
  const siteSettingsChildren: NavItem[] = [];
  const laborSettingsChildren: NavItem[] = [];
  const inventorySettingsChildren: NavItem[] = [];
  const salesSettingsChildren: NavItem[] = [];

  // organization settings routes

  if (isPermitted(user, [CboRole.SITE_VIEW, CboRole.SITE_EDIT])) {
    organizationSettingsChildren.push({
      title: i18next.t('navItems.sites'),
      href: routes.SITES,
      testid: 'sites-menu-item',
    });
  }
  if (isPermitted(user, [CboRole.FISCAL_CALENDAR_VIEW, CboRole.FISCAL_CALENDAR_EDIT])) {
    organizationSettingsChildren.push({
      title: i18next.t('navItems.fiscalCalendar'),
      href: routes.FISCAL_CALENDAR,
      testid: 'fiscal-calendar-menu-item',
    });
  }

  if (isPermitted(user, [CboRole.GL_ACCOUNTS_READ]) && cboGeneralLedgerAccounts && hasCoreAnalytics) {
    organizationSettingsChildren.push(
      {
        title: i18next.t('navItems.manageGeneralLedgerAccounts'),
        href: routes.MANAGE_GL_ACCOUNTS,
        testid: 'manage-gl-accounts',
      },
      {
        title: i18next.t('navItems.glAccountMapping'),
        href: routes.GL_ACCOUNT_MAPPING,
        testid: 'gl-account-mapping',
      }
    );
  }

  if (
    isPermitted(user, [CboRole.CUSTOM_METRIC_VIEW, CboRole.CUSTOM_METRIC_WRITER]) &&
    hasCoreAnalytics &&
    cboCustomMetrics
  ) {
    organizationSettingsChildren.push({
      title: i18next.t('navItems.customMetrics'),
      href: routes.CUSTOM_METRICS,
      testid: 'custom-metrics',
    });
  }

  if (
    isEligibletoShow(hasStarterPackage, cboTipShareDistribution) &&
    isPermitted(user, [CboRole.TIP_SHARE_CONFIG_EDIT, CboRole.TIP_SHARE_CONFIG_VIEW])
  ) {
    organizationSettingsChildren.push({
      title: i18next.t('navItems.tipShare'),
      href: routes.TIP_SHARE_SETTINGS,
      testid: 'tip-share-menu-item',
    });
  }

  if (organizationSettingsChildren.length > 0) {
    settingsRoutes.push({
      title: i18next.t('navItems.organizationSettings'),
      testid: 'organization-settings-menu-item',
      children: organizationSettingsChildren,
    });
  }

  // site settings routes

  if (isPermitted(user, [CboRole.SITE_VIEW, CboRole.SITE_EDIT])) {
    siteSettingsChildren.push({
      title: i18next.t('navItems.siteSettings'),
      href: routes.SITE_SETTINGS,
      testid: 'site-settings-menu-item',
    });
  }

  if (isPermitted(user, [CboRole.PAYROLL_CALENDAR_VIEW, CboRole.PAYROLL_CALENDAR_EDIT]) && !cboHideAlohaEssentialOrgs) {
    siteSettingsChildren.push({
      title: i18next.t('navItems.payrollCalendar'),
      href: routes.PAYROLL_CALENDAR,
      testid: 'payroll-calendar-menu-item',
    });
  }

  if (isPermitted(user, [CboRole.SITE_DAY_PART_VIEW, CboRole.SITE_DAY_PART_EDIT]) && hasStarterPackage) {
    siteSettingsChildren.push({
      title: i18next.t('navItems.dayParts'),
      href: routes.DAY_PARTS,
      testid: 'dayparts-menu-item',
    });
  }

  if (siteSettingsChildren.length > 0) {
    settingsRoutes.push({
      title: i18next.t('navItems.siteSettings'),
      testid: 'site-settings-menu-item-title',
      children: siteSettingsChildren,
    });
  }
  // labor settings routes
  // once roles are created those roles should be applied to laborSettingsSchedule
  laborSettingsChildren.push({
    title: i18next.t('navItems.laborSettingsSchedule'),
    href: routes.LABOR_SETTINGS_SCHEDULE,
    testid: 'labor-setting-schedule',
  });

  if (
    isPermitted(user, [CboRole.LRE_RULE_WRITER, CboRole.LRE_RULE_EXEC, CboRole.LRE_RULE_READER]) &&
    hasStarterPackage
  ) {
    laborSettingsChildren.push({
      title: i18next.t('navItems.laborRules'),
      href: routes.LABOR_RULES,
      testid: 'labor-rules-menu-item',
    });
  }

  if (
    isPermitted(user, [CboRole.JOBCODE_CONFIG_VIEW]) &&
    (!isCfcOrg || isPermitted(user, [CboRole.JOBCODE_CONFIG_ADMIN]))
  ) {
    laborSettingsChildren.push({
      title: i18next.t('navItems.jobCodeOverview'),
      href: routes.JOBCODE_CONFIGURATION_PAGE,
      testid: 'jobcode-configuration-overview',
    });
  }

  if (!cboHideAlohaEssentialOrgs && laborSettingsChildren.length > 0) {
    settingsRoutes.push({
      title: i18next.t('navItems.laborSettings'),
      testid: 'labor-settings-menu-item',
      children: laborSettingsChildren,
    });
  }

  // inventory settings routes
  // TODO: Remove the CBO_INVENTORY_VALUATION_CONFIG_EDITOR check and use CBO_INVENTORY_VALUATION_CONFIG_VIEWER once viewer role is remove for non-admin personas
  if (cboInventoryValuation && isPermitted(user, [CboRole.CBO_INVENTORY_VALUATION_CONFIG_EDITOR])) {
    inventorySettingsChildren.push({
      title: i18next.t('navItems.inventoryValuation'),
      href: routes.INVENTORY_VALUATION_SETTINGS,
      testid: 'inventory-valuation-menu-item',
    });
  }

  if (cboInventoryValuation && inventorySettingsChildren.length > 0) {
    settingsRoutes.push({
      title: i18next.t('navItems.inventorySettings'),
      testid: 'inventory-settings-menu-item',
      children: inventorySettingsChildren,
    });
  }

  // sales settings routes
  if (
    hasCoreAnalytics &&
    isPermitted(user, [CboRole.SALES_CONFIG_ADMIN, CboRole.SALES_CONFIG_READER, CboRole.SALES_CONFIG_WRITER])
  ) {
    salesSettingsChildren.push({
      title: i18next.t('navItems.salesDefinitions'),
      href: routes.SALES_DEFINITIONS_SETTINGS,
      testid: 'sales-definitions-settings',
    });
  }

  if (salesSettingsChildren.length > 0) {
    settingsRoutes.push({
      title: i18next.t('navItems.salesSettings'),
      testid: 'sales-settings',
      children: salesSettingsChildren,
    });
  }

  return settingsRoutes;
}
